/**
 * Helpers: Upsell
 * -----------------------------------------------------------------------------
 * Pairs upsell-only line items to the parent they were bought with.
 * - Paired by a token on both line items, unique per add so two of the same
 *   parent variant can't be confused. Line item keys can't be used, they
 *   change whenever a line item's properties do.
 *
 */
import {
  UPSELL_GROUP_PROPERTY,
  UPSELL_ONLY_TAG,
  UPSELL_PARENT_PROPERTY,
} from '~/config/const'
import { generateUUID } from '~/helpers/general'

/**
 * Get a line item's group token.
 * @param {Object} lineItem - Line item in cart response.
 * @returns {String}
 */
function getUpsellGroup(lineItem) {
  return lineItem?.properties?.[UPSELL_GROUP_PROPERTY] || ''
}

/**
 * Check if a product or line item is an upsell-only product.
 * - Line items carry product tags because the cart resource template merges
 *   them into the cart response.
 * @param {Object} object - Formatted product or line item in cart response.
 * @returns {Boolean}
 */
export function isUpsellOnly(object) {
  return Boolean(object?.tags?.includes(UPSELL_ONLY_TAG))
}

/**
 * Group line items by their group token.
 * @param {Array} items - Line items in cart response.
 * @returns {Object} Token keyed object of `parent` and `children`.
 */
function getUpsellGroups(items = []) {
  const groups = {}

  items.forEach((lineItem) => {
    const group = getUpsellGroup(lineItem)

    if (!group) {
      return
    }

    if (!groups[group]) {
      groups[group] = { children: [], parent: false }
    }

    if (isUpsellOnly(lineItem)) {
      groups[group].children.push(lineItem)
      return
    }

    groups[group].parent = lineItem
  })

  return groups
}

/**
 * Get the upsell line items belonging to a parent line item.
 * @param {Object} lineItem - Parent line item in cart response.
 * @param {Array} items - Line items in cart response.
 * @returns {Array}
 */
export function getUpsellsForItem(lineItem, items = []) {
  const group = getUpsellGroup(lineItem)

  if (!group || isUpsellOnly(lineItem)) {
    return []
  }

  return items.filter((item) => {
    return isUpsellOnly(item) && getUpsellGroup(item) === group
  })
}

/**
 * Remove upsell-only line items from an array of line items.
 * - Falsy entries are loading placeholders and are always kept.
 * @param {Array} items - Line items in cart response.
 * @returns {Array}
 */
export function withoutUpsellItems(items = []) {
  return items.filter((lineItem) => {
    return !lineItem || !isUpsellOnly(lineItem)
  })
}

/**
 * Remove upsell items from an add request payload.
 * - Upsell items carry a parent reference, so they can be identified before
 *   they're in the cart and have product tags to read.
 * @param {Array} items - Items in an add request payload.
 * @returns {Array}
 */
export function withoutUpsellPayloadItems(items = []) {
  return items.filter((item) => {
    return !item?.properties?.[UPSELL_PARENT_PROPERTY]
  })
}

/**
 * Build the items payload for an add request, tokenising the group.
 * - No token is added when there are no upsells, so parent line items merge
 *   as normal when bought on their own.
 * @param {Object} payload - Payload.
 * @param {Object} payload.parent - Parent item, requires `id`.
 * @param {Array} [payload.upsells] - Upsell items, each requires `id`.
 * @returns {Array}
 */
export function buildUpsellItems({ parent, upsells = [] } = {}) {
  const quantity = parent?.quantity || 1

  const items = [{
    ...parent,
    properties: { ...parent?.properties },
    quantity,
  }]

  if (!upsells.length) {
    return items
  }

  const group = generateUUID()
  items[0].properties[UPSELL_GROUP_PROPERTY] = group

  upsells.forEach((upsell) => {
    items.push({
      ...upsell,
      properties: {
        ...upsell.properties,
        [UPSELL_GROUP_PROPERTY]: group,
        [UPSELL_PARENT_PROPERTY]: String(parent.id),
      },
      quantity,
    })
  })

  return items
}

/**
 * Expand an update payload to include the upsells of any parent it targets.
 * - Upsell quantities always mirror their parent's.
 * - Property-only updates don't cascade as properties aren't inherited.
 * - Returns false when no upsells are affected so the caller can fall back to
 *   its normal update path.
 * @param {Array} items - Line items in cart response.
 * @param {Array|String} payload - `cart/updateItem` payload.
 * @returns {Object|Boolean} Line item key keyed object of quantities.
 */
export function getUpsellCascade(items = [], payload) {
  const targets = []

  if (typeof payload === 'string') {
    targets.push({ key: payload, quantity: 0 })
  } else if (Array.isArray(payload)) {
    payload.forEach(({ id, quantity }) => {
      if (typeof quantity !== 'number') {
        return
      }

      const lineItem = items.find((item) => {
        return item[typeof id === 'string' ? 'key' : 'id'] === id
      })

      if (!lineItem) {
        return
      }

      targets.push({ key: lineItem.key, quantity })
    })
  }

  if (!targets.length) {
    return false
  }

  const groups = getUpsellGroups(items)
  const updates = {}
  let cascaded = false

  targets.forEach(({ key, quantity }) => {
    updates[key] = quantity

    const lineItem = items.find((item) => item.key === key)
    const group = groups[getUpsellGroup(lineItem)]

    if (!group || group.parent?.key !== key) {
      return
    }

    group.children.forEach((child) => {
      updates[child.key] = quantity
      cascaded = true
    })
  })

  return cascaded ? updates : false
}

/**
 * Get the keys of upsell line items whose parent has left the cart.
 * - Upsell items with no group token are orphans too, they've arrived from
 *   outside the theme.
 * @param {Array} items - Line items in cart response.
 * @returns {Array}
 */
export function getOrphanedUpsellKeys(items = []) {
  const groups = getUpsellGroups(items)

  return items
    .filter((lineItem) => {
      if (!isUpsellOnly(lineItem)) {
        return false
      }

      return !groups[getUpsellGroup(lineItem)]?.parent
    })
    .map((lineItem) => lineItem.key)
}

/**
 * Get the cart status label for a batch of quantity updates.
 * @param {Array} items - Line items in cart response.
 * @param {Object} updates - Line item key keyed object of quantities.
 * @returns {String}
 */
export function getUpdatesStatusLabel(items = [], updates = {}) {
  const labels = Object.keys(updates).map((key) => {
    const lineItem = items.find((item) => item.key === key)

    if (!lineItem || updates[key] === lineItem.quantity) {
      return 'updating'
    }

    return updates[key] > lineItem.quantity ? 'adding' : 'removing'
  })

  const unique = [...new Set(labels)]

  return unique.length > 1 ? 'updating' : unique[0]
}
